<template>
  <StatusCard
    v-if="!dismissed"
    :status="type"
    :title="title"
    :message="message"
    :icon="computedIcon"
    :icon-size="24"
    :aria-label="ariaLabel || `${type} alert: ${message}`"
    :data-testid="`alert-banner-${type}`"
    role="alert"
    size="medium"
    class="alert-banner"
  >
    <template #actions>
      <v-btn
        v-if="dismissible"
        icon
        small
        class="alert-banner__dismiss"
        :aria-label="'Dismiss alert'"
        @click="handleDismiss"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </template>
  </StatusCard>
</template>

<script lang="ts">
import Vue from 'vue'
import StatusCard from '../StatusCard/StatusCard.vue'

type AlertType = 'warning' | 'error' | 'info' | 'success'

const typeIconMap: Record<AlertType, string> = {
  warning: 'mdi-alert',
  error: 'mdi-alert-circle',
  info: 'mdi-information',
  success: 'mdi-check-circle'
}

export default Vue.extend({
  name: 'AlertBanner',

  components: {
    StatusCard
  },

  props: {
    /** Alert message */
    message: {
      type: String,
      required: true
    },
    /** Alert type */
    type: {
      type: String as () => AlertType,
      default: 'info',
      validator: (value: string) => ['warning', 'error', 'info', 'success'].includes(value)
    },
    /** Optional alert title */
    title: {
      type: String,
      default: ''
    },
    /** Custom icon override */
    icon: {
      type: String,
      default: ''
    },
    /** Allow user to dismiss the alert */
    dismissible: {
      type: Boolean,
      default: false
    },
    /** Custom aria-label for accessibility */
    ariaLabel: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      dismissed: false
    }
  },

  computed: {
    computedIcon(): string {
      return this.icon || typeIconMap[this.type]
    }
  },

  methods: {
    handleDismiss() {
      this.dismissed = true
      this.$emit('dismiss')
    }
  }
})
</script>

<style scoped>
.alert-banner__dismiss {
  margin: -8px -8px -8px 0;
}
</style>
